<template>
  <div class="lgpd-page">
    <HomeHeader />

    <!-- Hero Section com Efeito Parallax -->
    <section class="hero-section">
      <div class="parallax-container">
        <div class="overlay"></div>
        <h1 class="hero-title">{{ t('lgpd.heroTitle') }}</h1>
        <div class="hero-subtitle">{{ t('lgpd.heroSubtitle') }}</div>
      </div>
    </section>

    <!-- Seção CTA -->
    <section class="cta-section">
      <div class="stats-watermark stats-watermark-1"></div>
      <div class="stats-watermark stats-watermark-2"></div>
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="cta-content">
            <h2>{{ t('lgpd.ctaTitle') }}</h2>
            <p>{{ t('lgpd.ctaText') }}</p>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <!-- Seção de Introdução -->
    <section class="intro-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="100">
          <div class="section-title">
            <span class="accent-line"></span>
            <h2>{{ t('lgpd.introTitle') }}</h2>
            <span class="accent-line"></span>
          </div>
          <div class="intro-content">
            <p class="intro-text">{{ t('lgpd.introText') }}</p>
            <p class="intro-text">{{ t('lgpd.introText2') }}</p>
            <p class="intro-text">{{ t('lgpd.introText3') }}</p>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <!-- Seção de Segurança da Informação -->
    <section class="security-section">
      <div class="watermark-side watermark-side-left"></div>
      <div class="container">
        <ScrollReveal direction="bottom" :delay="100">
          <div class="section-title">
            <span class="accent-line"></span>
            <h2>{{ t('lgpd.security.title') }}</h2>
            <span class="accent-line"></span>
          </div>
        </ScrollReveal>

        <div class="security-grid">
          <ScrollReveal direction="left" :delay="200">
            <div class="security-card">
              <div class="security-icon">
                <i class="fas fa-shield-alt"></i>
              </div>
              <div class="security-content">
                <p>{{ t('lgpd.security.card1.text') }}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" :delay="300">
            <div class="security-card">
              <div class="security-icon">
                <i class="fas fa-lock"></i>
              </div>
              <div class="security-content">
                <p>{{ t('lgpd.security.card2.text') }}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="left" :delay="400">
            <div class="security-card">
              <div class="security-icon">
                <i class="fas fa-users-cog"></i>
              </div>
              <div class="security-content">
                <p>{{ t('lgpd.security.card3.text') }}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" :delay="500">
            <div class="security-card">
              <div class="security-icon">
                <i class="fas fa-user-shield"></i>
              </div>
              <div class="security-content">
                <p>{{ t('lgpd.security.card4.text') }}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="left" :delay="600">
            <div class="security-card">
              <div class="security-icon">
                <i class="fas fa-chart-line"></i>
              </div>
              <div class="security-content">
                <p>{{ t('lgpd.security.card5.text') }}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" :delay="700">
            <div class="security-card">
              <div class="security-icon">
                <i class="fas fa-handshake"></i>
              </div>
              <div class="security-content">
                <p>{{ t('lgpd.security.card6.text') }}</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- Seção de Documentos -->
    <section class="documents-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="section-title">
            <span class="accent-line"></span>
            <h2>{{ t('lgpd.documents.title') }}</h2>
            <span class="accent-line"></span>
          </div>
        </ScrollReveal>

        <div class="documents-grid">
          <ScrollReveal direction="bottom" :delay="300">
            <div class="document-card">
              <div class="document-icon">
                <i class="fas fa-file-shield"></i>
              </div>
              <div class="document-content">
                <h3>{{ t('lgpd.documents.privacyPolicy.title') }}</h3>
                <p>{{ t('lgpd.documents.privacyPolicy.description') }}</p>
                <router-link to="/PoliticaPrivacidade" class="document-button">
                  <i class="fas fa-file-shield"></i>
                  {{ t('lgpd.buttons.accessDocument') }}
                </router-link>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="bottom" :delay="400">
            <div class="document-card">
              <div class="document-icon">
                <i class="fas fa-cookie-bite"></i>
              </div>
              <div class="document-content">
                <h3>{{ t('lgpd.documents.cookiesPolicy.title') }}</h3>
                <p>{{ t('lgpd.documents.cookiesPolicy.description') }}</p>
                <a href="/documentos_site/Declaração de Cookies - Uni Hospitalar.pdf" target="_blank" class="document-button">
                  <i class="fas fa-cookie-bite"></i>
                  {{ t('lgpd.buttons.accessDocument') }}
                </a>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="bottom" :delay="500">
            <div class="document-card">
              <div class="document-icon">
                <i class="fas fa-user-tie"></i>
              </div>
              <div class="document-content">
                <h3>{{ t('lgpd.documents.recruitmentNotice.title') }}</h3>
                <p>{{ t('lgpd.documents.recruitmentNotice.description') }}</p>
                <a href="/documentos_site/Aviso de Privacidade - Uni Hospitalar.pdf" class="document-button" target="_blank">
                  <i class="fas fa-user-tie"></i>
                  {{ t('lgpd.buttons.accessDocument') }}
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- Seção Área do Titular -->
    <section class="data-subject-section">
      <div class="stats-watermark-center"></div>
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="section-title light">
            <span class="accent-line"></span>
            <h2>{{ t('lgpd.dataSubject.title') }}</h2>
            <span class="accent-line"></span>
          </div>
          <p class="data-subject-intro">{{ t('lgpd.dataSubject.intro') }}</p>
        </ScrollReveal>

        <div class="data-subject-content">
          <ScrollReveal direction="left" :delay="300">
            <div class="rights-info">
              <h3>{{ t('lgpd.dataSubject.rightsTitle') }}</h3>
              <p>{{ t('lgpd.dataSubject.rightsText') }}</p>
              <p>{{ t('lgpd.dataSubject.rightsText2') }}</p>

              <h4>{{ t('lgpd.dataSubject.exerciseTitle') }}</h4>
              <p>{{ t('lgpd.dataSubject.exerciseText') }}</p>

              <!-- Contato do DPO -->
              <div class="dpo-contact">
                <div class="dpo-info">
                  <h4 class="dpo-title">{{ t('lgpd.dpoTitle') }}</h4>
                  <div class="dpo-details">
                    <div class="dpo-item">
                      <i class="fas fa-user-shield"></i>
                      <span class="dpo-label">{{ t('lgpd.dpoName') }}:</span>
                      <span class="dpo-value">Ítalo Morais - DPO Uni Hospitalar</span>
                    </div>
                    <div class="dpo-item">
                      <i class="fas fa-envelope"></i>
                      <span class="dpo-label">{{ t('lgpd.dpoEmail') }}:</span>
                      <a href="mailto:dpo@unihospitalar.com.br" class="dpo-email">
                        dpo@unihospitalar.com.br
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
          <br>
          <ScrollReveal direction="right" :delay="400">
            <div class="rights-form">
              <div class="form-container">
                <div class="form-header">
                  <div class="form-icon">
                    <i class="fas fa-user-check"></i>
                  </div>
                  <div class="form-title">
                    <h3>{{ t('lgpd.dataSubject.formTitle') }}</h3>
                    <p>{{ t('lgpd.dataSubject.formSubtitle') }}</p>
                  </div>
                </div>

                <!-- Mensagem de Sucesso -->
                <div v-if="requestSubmitted" class="success-message">
                  <div class="success-content">
                    <div class="success-icon">
                      <i class="fas fa-check-circle"></i>
                    </div>
                    <h3>{{ t('lgpd.dataSubject.messages.success.title') }}</h3>
                    <p>{{ t('lgpd.dataSubject.messages.success.description') }}</p>

                    <div class="response-info">
                      <div class="info-item">
                        <i class="fas fa-clock"></i>
                        <span><strong>{{ t('lgpd.dataSubject.messages.success.responseTime') }}</strong> {{ t('lgpd.dataSubject.messages.success.responseTimeValue') }}</span>
                      </div>
                      <div class="info-item">
                        <i class="fas fa-envelope"></i>
                        <span><strong>{{ t('lgpd.dataSubject.messages.success.contactEmail') }}</strong> {{ formData.email }}</span>
                      </div>
                      <div class="info-item">
                        <i class="fas fa-shield-alt"></i>
                        <span><strong>{{ t('lgpd.dataSubject.messages.success.dpoLabel') }}</strong> {{ t('lgpd.dataSubject.messages.success.dpoValue') }}</span>
                      </div>
                    </div>

                    <p class="success-note">
                      {{ t('lgpd.dataSubject.messages.success.note') }}
                    </p>

                    <button @click="resetForm" class="new-request-button">
                      <i class="fas fa-plus"></i>
                      {{ t('lgpd.dataSubject.messages.success.newRequestButton') }}
                    </button>
                  </div>
                </div>

                <!-- Mensagem de Erro -->
                <div v-if="submitError" class="error-message">
                  <div class="error-content">
                    <div class="error-icon">
                      <i class="fas fa-exclamation-triangle"></i>
                    </div>
                    <h3>{{ t('lgpd.dataSubject.messages.error.title') }}</h3>
                    <p>{{ submitError }}</p>
                    <button @click="submitError = ''" class="close-error-button">
                      <i class="fas fa-times"></i>
                      {{ t('lgpd.dataSubject.messages.error.closeButton') }}
                    </button>
                  </div>
                </div>

                <form v-if="!requestSubmitted" @submit.prevent="submitRightsForm" class="rights-form-content">
                  <div class="form-group">
                    <label for="name">{{ t('lgpd.dataSubject.form.name') }}<span class="required"> *</span></label>
                    <input type="text" id="name" v-model="formData.name" required
                      :placeholder="t('lgpd.dataSubject.form.placeholders.name')">
                  </div>

                  <div class="form-group">
                    <label for="email">{{ t('lgpd.dataSubject.form.email') }}<span class="required"> *</span></label>
                    <input type="email" id="email" v-model="formData.email" required
                      :placeholder="t('lgpd.dataSubject.form.placeholders.email')">
                  </div>

                  <div class="form-group">
                    <label for="phone">{{ t('lgpd.dataSubject.form.phone') }}<span class="required"> *</span></label>
                    <input type="tel" id="phone" v-model="formData.phone" required
                      :placeholder="t('lgpd.dataSubject.form.placeholders.phone')" @input="formatPhone"
                      @blur="validatePhone" maxlength="15" class="form-control">
                    <span v-if="phoneError" class="error-message">{{ t('lgpd.dataSubject.messages.phoneError') }}</span>
                  </div>

                  <div class="form-group">
                    <label for="relationship">{{ t('lgpd.dataSubject.form.relationship') }}<span class="required">
                        *</span></label>
                    <select id="relationship" v-model="formData.relationship" required>
                      <option value="">{{ t('lgpd.dataSubject.form.placeholders.relationship') }}</option>
                      <option value="client">{{ t('lgpd.dataSubject.form.relationshipOptions.client') }}</option>
                      <option value="employee">{{ t('lgpd.dataSubject.form.relationshipOptions.employee') }}</option>
                      <option value="exEmployee">{{ t('lgpd.dataSubject.form.relationshipOptions.exEmployee') }}
                      </option>
                      <option value="supplier">{{ t('lgpd.dataSubject.form.relationshipOptions.supplier') }}</option>
                      <option value="candidate">{{ t('lgpd.dataSubject.form.relationshipOptions.candidate') }}</option>
                      <option value="other">{{ t('lgpd.dataSubject.form.relationshipOptions.other') }}</option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label for="requestType">{{ t('lgpd.dataSubject.form.requestType') }}<span class="required">
                        *</span></label>
                    <select id="requestType" v-model="formData.requestType" required>
                      <option value="">{{ t('lgpd.dataSubject.form.placeholders.requestType') }}</option>
                      <option value="confirm">{{ t('lgpd.dataSubject.form.requestTypeOptions.confirm') }}</option>
                      <option value="access">{{ t('lgpd.dataSubject.form.requestTypeOptions.access') }}</option>
                      <option value="portability">{{ t('lgpd.dataSubject.form.requestTypeOptions.portability') }}
                      </option>
                      <option value="deletion">{{ t('lgpd.dataSubject.form.requestTypeOptions.deletion') }}</option>
                      <option value="correction">{{ t('lgpd.dataSubject.form.requestTypeOptions.correction') }}</option>
                      <option value="sharing">{{ t('lgpd.dataSubject.form.requestTypeOptions.sharing') }}</option>
                      <option value="revoke">{{ t('lgpd.dataSubject.form.requestTypeOptions.revoke') }}</option>
                      <option value="other">{{ t('lgpd.dataSubject.form.requestTypeOptions.other') }}</option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label for="details">{{ t('lgpd.dataSubject.form.details') }}</label>
                    <textarea id="details" v-model="formData.details" rows="4"
                      :placeholder="t('lgpd.dataSubject.form.placeholders.details')"></textarea>
                  </div>

                  <div class="form-disclaimer">
                    <p>{{ t('lgpd.dataSubject.form.disclaimer.text') }}
                      <router-link to="/PoliticaPrivacidade" target="_blank" class="terms-link">{{
                        t('lgpd.dataSubject.form.disclaimer.privacyPolicy') }}</router-link>
                      {{ t('lgpd.dataSubject.form.disclaimer.continuation') }}
                    </p>
                  </div>

                  <button type="submit" class="submit-button" :disabled="isSubmitting">
                    <i class="fas fa-paper-plane" v-if="!isSubmitting"></i>
                    <i class="fas fa-spinner fa-spin" v-else></i>
                    {{ isSubmitting ? t('lgpd.dataSubject.messages.submitting') : t('lgpd.dataSubject.form.submit') }}
                  </button>
                </form>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <HomeFooter />
  </div>
</template>

<script>
import HomeHeader from '@/components/HomeHeader.vue';
import HomeFooter from '@/components/HomeFooter.vue';
import ScrollReveal from '@/components/ScrollReveal.vue';
import { useLGPDTranslation } from '@/composables/useLGPDTranslation.js';

export default {
  components: {
    HomeHeader,
    HomeFooter,
    ScrollReveal
  },
  setup() {
    const { t, currentLanguage } = useLGPDTranslation()

    return {
      t,
      currentLanguage
    }
  },
  data() {
    return {
      selectedLanguage: 'pt',
      sidebarOpen: false,
      isSubmitting: false,
      requestSubmitted: false,
      submitError: '',
      formData: {
        name: '',
        email: '',
        phone: '',
        relationship: '',
        requestType: '',
        details: ''
      },
      phoneError: false
    }
  },
  methods: {
    changeLanguage(event) {
      const value = typeof event === 'string' ? event : event.target.value;
      this.selectedLanguage = value;
      console.log(`Idioma selecionado: ${value}`);
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    async submitRightsForm() {
      this.isSubmitting = true;
      this.submitError = '';
      this.validatePhone();

if (this.phoneError) {
  this.isSubmitting = false;
  return;
}

      try {
        // Preparar dados da solicitação LGPD
        const lgpdRequestData = {
          name: this.formData.name,
          email: this.formData.email,
          phone: this.formData.phone,
          relationship: this.formData.relationship,
          requestType: this.formData.requestType,
          details: this.formData.details
        };

        console.log('📤 Enviando solicitação LGPD para API...');
        console.log('📋 Dados:', lgpdRequestData);

        // Fazer requisição para a API
        const apiUrl = this.getApiUrl();
        const response = await fetch(`${apiUrl}/api/lgpd-requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(lgpdRequestData)
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Erro ao enviar solicitação LGPD');
        }

        // Sucesso - não há códigos de protocolo para LGPD
        this.requestSubmitted = true;
        this.isSubmitting = false;

        console.log('✅ Solicitação LGPD enviada com sucesso!');
        console.log('📧 Confirmação enviada para:', this.formData.email);

        // Scroll suave para a seção de sucesso
        this.$nextTick(() => {
          const successSection = document.querySelector('.success-message');
          if (successSection) {
            successSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        });

      } catch (error) {
        console.error('❌ Erro ao enviar solicitação LGPD:', error);
        this.isSubmitting = false;
        this.submitError = `Erro ao enviar solicitação: ${error.message}`;
      }
    },
    getApiUrl() {
      // Detectar ambiente
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:3001'; // Desenvolvimento
      } else if (window.location.hostname === 'www.uniteste.cloud' || window.location.hostname === 'uniteste.cloud') {
        return 'https://paineladm.uniteste.cloud'; // Homologação
      } else {
        return 'https://paineladm.unihospitalar.com.br'; // Produção
      }
    },

    formatPhone(event) {
    let value = event.target.value.replace(/\D/g, '');

    if (value.length > 11) {
      value = value.substring(0, 11);
    }

    if (value.length <= 2) {
      // apenas DDD
    } else if (value.length <= 6) {
      value = value.replace(/(\d{2})(\d+)/, '($1) $2');
    } else if (value.length <= 10) {
      // Telefone fixo: (XX) XXXX-XXXX
      value = value.replace(/(\d{2})(\d{4})(\d+)/, '($1) $2-$3');
    } else {
      // Celular: (XX) XXXXX-XXXX
      value = value.replace(/(\d{2})(\d{5})(\d+)/, '($1) $2-$3');
    }

    this.formData.phone = value;
  },

  validatePhone() {
    const phone = this.formData.phone;

    // Regex: (XX) XXXX-XXXX ou (XX) XXXXX-XXXX
    const regexFixo = /^\(\d{2}\) \d{4}-\d{4}$/;
    const regexCelular = /^\(\d{2}\) \d{5}-\d{4}$/;

    this.phoneError = !(regexFixo.test(phone) || regexCelular.test(phone));
  },

    resetForm() {
      this.formData = {
        name: '',
        email: '',
        phone: '',
        relationship: '',
        requestType: '',
        details: ''
      };
      this.phoneError = false;
      this.requestSubmitted = false;
      this.submitError = '';
    }
  },
  watch: {
  'formData.phone'(newVal) {
    // Se estiver vazio, limpa o erro
    if (!newVal) {
      this.phoneError = false;
      return;
    }

    const regexFixo = /^\(\d{2}\) \d{4}-\d{4}$/;
    const regexCelular = /^\(\d{2}\) \d{5}-\d{4}$/;
    this.phoneError = !(regexFixo.test(newVal) || regexCelular.test(newVal));
  }
}
}
</script>

<style scoped>
/* Estilos Gerais */
.lgpd-page {
  font-family: 'Matter', 'Segoe UI', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

section {
  padding: 80px 0;
}

/* Hero Section com Parallax */
.hero-section {
  position: relative;
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  border-bottom: 4px solid #931414;
}

.parallax-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/header-lgpd.png');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(147, 20, 20, 0.7), rgba(0, 0, 0, 0.7));
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 2px;
  animation: fadeInUp 1.5s ease-out;
}

.hero-subtitle {
  font-size: 1.5rem;
  font-weight: 300;
  position: relative;
  z-index: 2;
  animation: fadeInUp 1.5s ease-out 0.3s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Seção CTA */
.cta-section {
  background: linear-gradient(135deg, #f0f0f0, #f0f0f0);
  text-align: center;
  padding: 60px 0;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 1;
}

.cta-content h2 {
  font-size: 2.2rem;
  color: #470404;
  margin-bottom: 20px;
  font-weight: 700;
  line-height: 1.3;
}

.cta-content p {
  font-size: 1.2rem;
  color: #696969;
  max-width: 800px;
  margin: 0 auto;
}

/* Seção de Introdução */
.intro-section {
  background-color: #f0f0f0;
  text-align: center;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
}

.section-title h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #931414;
  margin: 0 20px;
}

.section-title.light h2 {
  color: #ffffff;
}

.accent-line {
  height: 3px;
  width: 70px;
  background-color: #931414;
}

.section-title.light .accent-line {
  background-color: #ffffff;
}

.intro-content {
  max-width: 900px;
  margin: 0 auto;
}

.intro-text {
  font-size: 1.1rem;
  color: #555;
  line-height: 1.8;
  margin-bottom: 25px;
  text-align: justify;
}

/* Seção de Segurança */
.security-section {
  background: linear-gradient(225deg, #ffffff, #f0f0f0);
  position: relative;
  overflow: hidden;
  /* margin-bottom: 50px; */
}

/* Símbolo da marca duplicado ao fundo, desmaiado, nos dois cantos opostos */
.stats-watermark {
  position: absolute;
  top: 0;
  width: 700px;
  height: 700px;
  background-image: url('@/assets/uni-simbolo-outline.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.16;
  pointer-events: none;
  z-index: 0;
}

.stats-watermark-1 {
  left: -181px;
  top: -203px;
  mask-image: linear-gradient(to bottom right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(to bottom right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
}

.stats-watermark-2 {
  right: -203px;
  left: auto;
  top: auto;
  bottom: -226px;
  mask-image: linear-gradient(to top left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(to top left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
}

@media (min-width: 769px) and (max-height: 820px) {
  .stats-watermark-2 {
    display: none;
  }
}

@media (max-width: 768px) {
  .stats-watermark-2 {
    display: none;
  }
}

/* Símbolo da marca único, sangrando pela lateral esquerda */
.watermark-side {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 1008px;
  height: 1008px;
  background-image: url('@/assets/uni-simbolo-outline.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.15;
  pointer-events: none;
  z-index: 0;
}

.watermark-side-left {
  left: -399px;
  mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
}

/* Símbolo da marca único, centralizado e expandido (fundo sólido vermelho) */
.stats-watermark-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/uni-simbolo-outline-branco.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 1600px;
  opacity: 0.14;
  pointer-events: none;
  z-index: 0;
}

.security-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 90px;
  align-items: stretch;
  position: relative;
  z-index: 1;
}

.security-card {
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-left: 4px solid #931414;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.security-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.security-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.security-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #931414, #470404);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 2rem;
  margin-bottom: 20px;
}

.security-content h3 {
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
}

.security-content p {
  color: #696969;
  line-height: 1.6;
  margin: 0;
  text-align: justify;
}

/* Seção de Documentos */
.documents-section {
  background: linear-gradient(135deg, #f0f0f0, #f0f0f0);
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin: 40px 0 50px 0;
  align-items: stretch;
}

.document-card {
  background: white;
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.document-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.document-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.document-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #931414, #470404);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 2.5rem;
  margin: 0 auto 20px;
}

.document-content h3 {
  font-size: 1.4rem;
  background: linear-gradient(-135deg, #300303, #931414);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 15px;
  font-weight: 600;
}

.document-content p {
  color: #696969;
  margin-bottom: 25px;
  line-height: 1.6;
  flex: 1;
}

/* .document-button está na regra global de glass (public/styles.css),
   mas o fundo aqui (.document-card) é branco chapado, então precisa de
   opacidade alta pra não ficar sujo/desbotado */
.document-button {
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.32), rgba(71, 4, 4, 0.32)) !important;
  backdrop-filter: blur(20px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(200%) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
  padding: 12px 25px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(147, 20, 20, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  margin-top: auto;
}

.document-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.document-button i {
  position: relative;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
}

.document-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(147, 20, 20, 0.4);
}

/* Seção Área do Titular */
.data-subject-section {
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  position: relative;
  overflow: hidden;
}

.data-subject-intro {
  text-align: center;
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 50px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.7;
}

.data-subject-content {
  display: grid;
  gap: 15px;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.rights-info {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.rights-info h3 {
  font-size: 1.8rem;
  margin-bottom: 20px;
  color: white;
  font-weight: 700;
}

.rights-info h4 {
  font-size: 1.4rem;
  margin: 30px 0 15px 0;
  color: white;
  font-weight: 600;
}

.rights-info p {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.7;
  margin-bottom: 20px;
}

/* Estilos para o contato do DPO */
.dpo-contact {
  margin-top: 30px;
  padding: 25px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  border-left: 4px solid #931414;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.dpo-info {
  text-align: center;
}

.dpo-title {
  font-size: 1.2rem !important;
  color: #931414 !important;
  margin: 0 0 20px 0 !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 1px !important;
}

.dpo-details {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
}

.dpo-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  color: #333;
}

.dpo-item i {
  color: #931414;
  font-size: 1.1rem;
  width: 20px;
  text-align: center;
}

.dpo-label {
  font-weight: 600;
  color: #333;
}

.dpo-value {
  color: #555;
  font-weight: 500;
}

.dpo-email {
  color: #931414;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
}

.dpo-email::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: -2px;
  left: 0;
  background-color: #931414;
  transition: width 0.3s ease;
}

.dpo-email:hover {
  color: #470404;
  text-decoration: none !important;
  transform: translateY(-2px);
}

.dpo-email:hover::after {
  width: 100%;
}

/* Formulário de Direitos */
.rights-form {
  background: white;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  max-width: 800px;
  border-radius: 15px;
  margin: 0 auto;
  width: 100%;
}

.required {
  color: #dc3545;
  font-weight: bold;
}

.form-container {
  height: 100%;
}

.form-header {
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  border-top-left-radius: 13px;
  border-top-right-radius: 13px;
}

.form-icon {
  font-size: 2rem;
  background: rgba(255, 255, 255, 0.2);
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  flex-shrink: 0;
}

.form-title h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 5px 0;
}

.form-title p {
  font-size: 0.9rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
}

.rights-form-content {
  padding: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #f0f0f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #931414;
  box-shadow: 0 0 0 3px rgba(147, 20, 20, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.form-disclaimer {
  background: #f0f0f0;
  border-left: 4px solid #931414;
  padding: 15px;
  border-radius: 5px;
  margin: 25px 0;
}

.form-disclaimer p {
  font-size: 0.85rem;
  color: #696969;
  line-height: 1.5;
  margin: 0;
  text-align: justify;
}

.terms-link {
  color: #931414;
  text-decoration: none;
  font-weight: 600;
}

.terms-link:hover {
  text-decoration: underline;
  color: #470404;
}

/* .submit-button está na regra global de glass, mas o fundo aqui
   (.rights-form-content) é branco chapado, então precisa de opacidade
   alta pra não ficar sujo/desbotado */
.submit-button {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.32), rgba(71, 4, 4, 0.32)) !important;
  backdrop-filter: blur(20px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(200%) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
  padding: 15px 30px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 1rem;
  width: 100%;
  box-shadow: 0 5px 15px rgba(147, 20, 20, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.submit-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.submit-button i {
  position: relative;
  background-image: none !important;
  -webkit-background-clip: initial !important;
  background-clip: initial !important;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(147, 20, 20, 0.4);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Responsividade */
@media (max-width: 1200px) {
  .container {
    padding: 0 40px;
  }

  .data-subject-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .documents-grid {
    grid-template-columns: 1fr;
    max-width: 800px;
    margin: 40px auto;
    row-gap: 100px;
  }

  .rights-form {
    max-width: 100%;
  }
}

@media (max-width: 992px) {
  .hero-title {
    font-size: 3rem;
  }

  .hero-subtitle {
    font-size: 1.3rem;
  }

  section {
    padding: 60px 0;
  }

  .cta-content h2 {
    font-size: 2rem;
  }

  .security-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 60vh;
    min-height: 400px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .section-title h2 {
    font-size: 2rem;
  }

  .intro-text {
    font-size: 1rem;
    text-align: left;
  }

  .cta-content h2 {
    font-size: 1.8rem;
  }

  .cta-content p {
    font-size: 1.1rem;
  }

  .security-grid {
    grid-template-columns: 1fr;
  }

  .documents-grid {
    grid-template-columns: 1fr;
  }

  .data-subject-content {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .dpo-contact {
    margin-top: 25px;
    padding: 20px;
  }

  .dpo-title {
    font-size: 1.1rem;
  }

  .dpo-details {
    gap: 12px;
  }

  .dpo-item {
    font-size: 0.95rem;
    flex-wrap: wrap;
    justify-content: center;
    text-align: center;
  }

  .form-header {
    padding: 20px;
    gap: 15px;
  }

  .form-icon {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  .form-title h3 {
    font-size: 1.3rem;
  }

  .rights-form-content {
    padding: 25px;
  }
}

@media (max-width: 576px) {
  .container {
    padding: 0 20px;
  }

  /* Ajustes para mensagens de sucesso/erro em mobile */
  .success-content,
  .error-content {
    padding: 25px 20px;
  }

  .success-icon,
  .error-icon {
    width: 60px;
    height: 60px;
    font-size: 2rem;
  }

  .success-content h3,
  .error-content h3 {
    font-size: 1.5rem;
  }

  .response-info {
    padding: 20px;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    text-align: left;
    padding: 12px;
  }

  .info-item i {
    margin-right: 0;
    margin-bottom: 5px;
  }

  .new-request-button,
  .close-error-button {
    padding: 10px 20px;
    font-size: 0.9rem;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .section-title h2 {
    font-size: 1.8rem;
  }

  .accent-line {
    width: 40px;
  }

  .cta-content h2 {
    font-size: 1.6rem;
  }

  .security-card,
  .document-card {
    padding: 25px;
  }

  .security-icon,
  .document-icon {
    width: 60px;
    height: 60px;
    font-size: 1.8rem;
  }

  .dpo-item {
    flex-direction: column;
    gap: 5px;
  }

  .dpo-email {
    word-break: break-all;
  }

  .form-header {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .rights-form-content {
    padding: 20px;
  }

  .form-disclaimer {
    padding: 12px;
  }

  .form-disclaimer p {
    font-size: 0.8rem;
  }
}

/* Animações adicionais */
@keyframes float {
  0% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }

  100% {
    transform: translateY(0px);
  }
}

.security-icon i,
.document-icon i {
  animation: float 3s ease-in-out infinite;
}

/* Animação para o botão de envio */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(147, 20, 20, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0);
  }
}

.submit-button:hover:not(:disabled) {
  animation: pulse 1.5s infinite;
}

/* Efeito de destaque para os cards */
.security-card:nth-child(1) {
  border-left-color: #931414;
}

.security-card:nth-child(2) {
  border-left-color: #2C5EAE;
}

.security-card:nth-child(3) {
  border-left-color: #2CAE6F;
}

.security-card:nth-child(4) {
  border-left-color: #AE8A2C;
}

.security-card:nth-child(5) {
  border-left-color: #9C27B0;
}

.security-card:nth-child(6) {
  border-left-color: #FF9800;
}

.security-card:nth-child(1) .security-icon {
  background: linear-gradient(135deg, #931414, #470404);
}

.security-card:nth-child(2) .security-icon {
  background: linear-gradient(135deg, #2C5EAE, #5596ff);
}

.security-card:nth-child(3) .security-icon {
  background: linear-gradient(135deg, #2CAE6F, #55ff9d);
}

.security-card:nth-child(4) .security-icon {
  background: linear-gradient(135deg, #AE8A2C, #ffcc55);
}

.security-card:nth-child(5) .security-icon {
  background: linear-gradient(135deg, #9C27B0, #E91E63);
}

.security-card:nth-child(6) .security-icon {
  background: linear-gradient(135deg, #FF9800, #FF5722);
}

/* Efeito de hover para os cards de segurança */
.security-card:hover .security-icon {
  transform: scale(1.1);
  transition: transform 0.3s ease;
}

/* Ajustes para dispositivos muito pequenos */
@media (max-width: 400px) {
  .hero-title {
    font-size: 1.8rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .section-title h2 {
    font-size: 1.5rem;
    margin: 0 10px;
  }

  .accent-line {
    width: 30px;
  }

  .cta-content h2 {
    font-size: 1.4rem;
  }

  .cta-content p {
    font-size: 1rem;
  }

  .intro-text {
    font-size: 0.95rem;
  }

  .security-card,
  .document-card {
    padding: 20px;
  }

  .security-icon,
  .document-icon {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  .security-content h3,
  .document-content h3 {
    font-size: 1.2rem;
  }

  .rights-info {
    padding: 20px;
  }

  .rights-info h3 {
    font-size: 1.5rem;
  }

  .rights-info h4 {
    font-size: 1.2rem;
  }

  .dpo-contact {
    padding: 15px;
  }

  .dpo-title {
    font-size: 1rem;
  }

  .form-header {
    padding: 15px;
  }

  .form-icon {
    width: 45px;
    height: 45px;
    font-size: 1.3rem;
  }

  .form-title h3 {
    font-size: 1.2rem;
  }

  .form-title p {
    font-size: 0.85rem;
  }

  .rights-form-content {
    padding: 15px;
  }

  .form-group {
    margin-bottom: 15px;
  }

  .form-group label {
    font-size: 0.9rem;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    padding: 10px 12px;
    font-size: 0.95rem;
  }

  .form-disclaimer {
    padding: 10px;
  }

  .form-disclaimer p {
    font-size: 0.75rem;
  }

  .submit-button {
    padding: 12px 25px;
    font-size: 0.95rem;
  }
}

/* Correções para o parallax em dispositivos móveis */
@media (max-width: 768px) {
  .parallax-container {
    background-attachment: scroll;
  }
}

/* Animação para entrada dos elementos */
.security-card,
.document-card {
  opacity: 0;
  animation: slideInUp 0.6s ease forwards;
}

.security-card:nth-child(1) {
  animation-delay: 0.1s;
}

.security-card:nth-child(2) {
  animation-delay: 0.2s;
}

.security-card:nth-child(3) {
  animation-delay: 0.3s;
}

.security-card:nth-child(4) {
  animation-delay: 0.4s;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Efeito de foco nos inputs */
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  transform: translateY(-2px);
}

/* Mensagem de Sucesso */
.success-message {
  padding: 0;
  margin-bottom: 20px;
}

.success-content {
  background: linear-gradient(135deg, #d4edda, #c3e6cb);
  border: 2px solid #28a745;
  border-radius: 15px;
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(40, 167, 69, 0.2);
}

.success-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28a745, #20c997);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 20px;
  color: white;
  font-size: 2.5rem;
  box-shadow: 0 5px 15px rgba(40, 167, 69, 0.3);
}

.success-content h3 {
  color: #155724;
  margin-bottom: 15px;
  font-size: 1.8rem;
  font-weight: 700;
}

.success-content p {
  color: #155724;
  margin-bottom: 20px;
  font-size: 1.1rem;
}

/* Informações de resposta LGPD */
.response-info {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 25px;
  margin: 20px 0;
  border-left: 4px solid #28a745;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.1);
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 1rem;
  color: #155724;
  padding: 10px 15px;
  background: rgba(40, 167, 69, 0.05);
  border-radius: 8px;
  border-left: 3px solid #28a745;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item i {
  color: #28a745;
  font-size: 1.2rem;
  margin-right: 12px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.info-item span {
  color: #155724;
  font-weight: 500;
  line-height: 1.4;
}

.info-item strong {
  color: #0f4229;
  font-weight: 700;
}

.success-note {
  font-size: 0.95rem !important;
  font-style: italic;
  margin-top: 20px !important;
  background: rgba(255, 255, 255, 0.6);
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #28a745;
}

.new-request-button {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.9), rgba(32, 201, 151, 0.9));
  backdrop-filter: blur(20px) saturate(200%);
  -webkit-backdrop-filter: blur(20px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: white;
  padding: 12px 25px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.new-request-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.new-request-button i {
  position: relative;
}

.new-request-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(40, 167, 69, 0.3);
  background: linear-gradient(135deg, rgba(33, 136, 56, 0.95), rgba(30, 165, 122, 0.95));
}

/* Mensagem de Erro */
.error-message {
  padding: 0;
  margin-bottom: 20px;
  color: red;
  font-size: 0.875rem;
  margin-top: 4px;
  display: block;
}

.error-content {
  background: linear-gradient(135deg, #f8d7da, #f5c6cb);
  border: 2px solid #dc3545;
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(220, 53, 69, 0.2);
}

.error-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dc3545, #e74c3c);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 20px;
  color: white;
  font-size: 2.5rem;
  box-shadow: 0 5px 15px rgba(220, 53, 69, 0.3);
}

.error-content h3 {
  color: #721c24;
  margin-bottom: 15px;
  font-size: 1.8rem;
  font-weight: 700;
}

.error-content p {
  color: #721c24;
  margin-bottom: 20px;
  font-size: 1.1rem;
}

.close-error-button {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.9), rgba(231, 76, 60, 0.9));
  backdrop-filter: blur(20px) saturate(200%);
  -webkit-backdrop-filter: blur(20px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.close-error-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.close-error-button i {
  position: relative;
}

.close-error-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(220, 53, 69, 0.3);
  background: linear-gradient(135deg, rgba(200, 35, 51, 0.95), rgba(220, 38, 38, 0.95));
}

/* Estilo para links dentro do texto */
.intro-text a,
.rights-info a {
  color: #931414;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;
}

.intro-text a:hover,
.rights-info a:hover {
  color: #470404;
  text-decoration: underline;
}

/* Ajuste para o texto da seção de direitos em dispositivos móveis */
@media (max-width: 768px) {
  .data-subject-intro {
    font-size: 1.1rem;
    text-align: left;
  }

  .rights-info p {
    text-align: left;
  }
}

/* Scrollbar personalizada */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #931414;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #470404;
}
</style>
